import { createContext, useContext, type CSSProperties, type ReactNode } from "react";

import classNames from "classnames";

import { colorsPalettes } from "@similarweb/styles";

export type HeatMapData = Record<string, Record<string, number>>;

export type ColorScale = {
    colors: string[];
    thresholds: number[];
};

type HeatMapCssVariableName =
    | "--heatmap-cell-min-width"
    | "--heatmap-cell-height"
    | "--heatmap-gap"
    | "--heatmap-header-row-size"
    | "--heatmap-axis-inline-padding"
    | "--heatmap-x-axis-marker-height"
    | "--heatmap-x-axis-marker-width"
    | "--heatmap-x-axis-marker-gap"
    | "--heatmap-x-axis-marker-offset-bottom"
    | "--heatmap-x-axis-marker-color"
    | "--heatmap-x-axis-marker-opacity"
    | "--heatmap-y-axis-inline-padding"
    | "--heatmap-cell-radius"
    | "--heatmap-cell-cursor"
    | "--heatmap-cell-hover-opacity"
    | "--heatmap-legend-gap"
    | "--heatmap-legend-margin-top"
    | "--heatmap-scroll-fade-size"
    | "--heatmap-scroll-fade-color"
    | "--heatmap-scrollbar-clearance"
    | "--heatmap-surface-color";

export type HeatMapStyle = CSSProperties & Partial<Record<HeatMapCssVariableName, string | number>>;

export type GetCellColor = (value: number, rowKey?: string) => string;

export type ScaleStrategy = "global" | "row";

export interface IHeatMapContextValue {
    data: HeatMapData;
    rows: string[];
    columns: string[];
    getColor: GetCellColor;
    colorScale: ColorScale;
    cellMinWidth: number;
    cellHeight: number;
    gap: number;
}

export interface IHeatMapProps {
    data: HeatMapData;
    colorScale?: ColorScale;
    getColor?: GetCellColor;
    scaleStrategy?: ScaleStrategy;
    cellMinWidth?: number;
    cellHeight?: number;
    gap?: number;
    className?: string;
    style?: HeatMapStyle;
    children: ReactNode;
}

export interface ITooltipCell {
    rowKey: string;
    colKey: string;
    value: number;
}

export type HeatMapTooltipContent = ReactNode;

export interface IXAxisConfig {
    labelEvery?: number;
    renderLabel?: (key: string, index: number) => ReactNode;
}

export interface IYAxisConfig {
    position?: "left" | "right";
    header?: ReactNode;
    renderLabel?: (key: string, index: number) => ReactNode;
}

export interface IGridProps {
    formatTooltip?: (cell: ITooltipCell) => HeatMapTooltipContent;
    onCellClick?: (cell: ITooltipCell) => void;
    xAxis?: IXAxisConfig;
    yAxis?: IYAxisConfig;
    initialScrollPosition?: "start" | "end";
}

export interface ILegendProps {
    minLabel?: ReactNode;
    maxLabel?: ReactNode;
}

export const DEFAULT_COLORS = [
    colorsPalettes.carbon[25],
    colorsPalettes.green[100],
    colorsPalettes.green[300],
    colorsPalettes.green["400_P"],
    colorsPalettes.green[500],
    colorsPalettes.green[600],
    colorsPalettes.green[700],
];

export const buildNormalizedScale = (colors: string[], data: HeatMapData): ColorScale => {
    if (colors.length < 2) {
        return { colors, thresholds: [] };
    }

    let posMin = Infinity;
    let max = -Infinity;

    for (const row of Object.values(data)) {
        for (const value of Object.values(row ?? {})) {
            if (value > 0) {
                if (value < posMin) {
                    posMin = value;
                }
                if (value > max) {
                    max = value;
                }
            }
        }
    }

    if (!Number.isFinite(posMin) || posMin === max) {
        return { colors, thresholds: [] };
    }

    const buckets = colors.length - 1;
    const lnMin = Math.log(posMin);
    const lnMax = Math.log(max);
    const step = (lnMax - lnMin) / buckets;
    const thresholds = Array.from({ length: buckets }, (_, i) => Math.exp(lnMin + step * (i + 1)));

    return { colors, thresholds };
};

export const HeatMapContext = createContext<IHeatMapContextValue | null>(null);

export const useHeatMapContext = () => {
    const context = useContext(HeatMapContext);

    if (!context) {
        throw new Error("HeatMap compound components must be used within <HeatMap>");
    }

    return context;
};

const FALLBACK_COLOR = colorsPalettes.carbon[25];

export const buildGetColor = (colorScale: ColorScale) => {
    const { colors, thresholds } = colorScale;

    if (colors.length === 0) {
        return () => FALLBACK_COLOR;
    }

    const noDataColor = colors[0];
    const valueColors = colors.slice(1);

    if (valueColors.length === 0) {
        return () => noDataColor;
    }

    const lastValueColor = valueColors[valueColors.length - 1];

    return (value: number) => {
        if (value <= 0) {
            return noDataColor;
        }

        for (let index = 0; index < thresholds.length; index += 1) {
            if (value <= thresholds[index]) {
                return valueColors[index] ?? lastValueColor;
            }
        }

        return lastValueColor;
    };
};

export const buildRowNormalizedScales = (colors: string[], data: HeatMapData): Record<string, ColorScale> => {
    const scales: Record<string, ColorScale> = {};

    for (const [rowKey, row] of Object.entries(data)) {
        scales[rowKey] = buildNormalizedScale(colors, { [rowKey]: row });
    }

    return scales;
};

export const buildRowGetColor = (colors: string[], scales: Record<string, ColorScale>): GetCellColor => {
    const fallbackScale: ColorScale = { colors, thresholds: [] };
    const cache: Record<string, (value: number) => string> = {};
    const fallback = buildGetColor(fallbackScale);

    return (value, rowKey) => {
        if (rowKey === undefined) {
            return fallback(value);
        }

        if (!cache[rowKey]) {
            const scale = scales[rowKey];
            cache[rowKey] = scale ? buildGetColor(scale) : fallback;
        }

        return cache[rowKey](value);
    };
};

export { classNames };
